// src/pages/tenant/admin/AdminTenantDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';

function mapTenantStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'ACTIVE':
    case 'TENANT_STATUS_ACTIVE':
      return 'success';
    case 'PENDING':
    case 'TENANT_STATUS_PENDING':
      return 'warning';
    case 'SUSPENDED':
    case 'TENANT_STATUS_SUSPENDED':
      return 'error';
    case 'ARCHIVED':
    case 'TENANT_STATUS_ARCHIVED':
      return 'secondary';
    case 'DELETED':
    case 'TENANT_STATUS_DELETED':
      return 'error';
    default:
      return 'secondary';
  }
}

function mapTenantTypeToVariant(
  type: string,
): 'info' | 'primary' | 'warning' | 'secondary' | 'error' | 'success' {
  switch (type) {
    case 'PERSONAL':
    case 'TENANT_TYPE_PERSONAL':
      return 'info';
    case 'TEAM':
    case 'TENANT_TYPE_TEAM':
      return 'info';
    case 'ORGANIZATION':
    case 'TENANT_TYPE_ORGANIZATION':
      return 'primary';
    case 'ENTERPRISE':
    case 'TENANT_TYPE_ENTERPRISE':
      return 'warning';
    case 'DEDICATED':
    case 'TENANT_TYPE_DEDICATED':
      return 'warning';
    default:
      return 'secondary';
  }
}

function normalizeStatus(status: string): string {
  return status.replace('TENANT_STATUS_', '');
}

function normalizeType(type: string): string {
  return type.replace('TENANT_TYPE_', '');
}

function normalizeIsolation(level: string): string {
  return level.replace('TENANT_ISOLATION_LEVEL_', '');
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

function formatBytes(bytes: number | undefined | null): string {
  if (bytes == null || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatCurrency(value: number | undefined | null): string {
  if (value == null) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value: number | undefined | null): string {
  if (value == null) return '--';
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Admin detailed view of a single tenant with management actions.
 * Route: /admin/tenants/:id
 */
export const AdminTenantDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const crud = useTenantTenant();
  const { t } = useI18n();
  const item = crud.currentItem as any;

  const [analytics, setAnalytics] = useState<any>(null);
  const [suspensionHistory, setSuspensionHistory] = useState<any[]>([]);
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Quota form state
  const [quotaForm, setQuotaForm] = useState<Record<string, number>>({
    max_members: 0,
    max_products: 0,
    max_api_calls_per_month: 0,
    max_storage_bytes: 0,
    max_bandwidth_bytes: 0,
    max_workflows: 0,
    max_integrations: 0,
    max_custom_fields: 0,
  });

  useEffect(() => {
    if (id) {
      crud.GetTenant(id).catch(() => {});
      store.GetTenantAnalytics(id).then((res: any) => {
        setAnalytics(res?.analytics ?? null);
      }).catch(() => {
        setAnalytics(null);
      });
      store.GetTenantSuspensionHistory(id).then((res: any) => {
        const items = res?.suspensions?.items;
        setSuspensionHistory(Array.isArray(items) ? items : []);
      }).catch(() => {
        setSuspensionHistory([]);
      });
    }
    return () => {
      crud.clearCurrent();
    };
  }, [id]);

  // Sync quota form when item loads
  useEffect(() => {
    if (item?.limits || item?.quotas) {
      const source = item.limits || item.quotas || {};
      setQuotaForm({
        max_members: source.max_members ?? 0,
        max_products: source.max_products ?? 0,
        max_api_calls_per_month: source.max_api_calls_per_month ?? 0,
        max_storage_bytes: source.max_storage_bytes ?? 0,
        max_bandwidth_bytes: source.max_bandwidth_bytes ?? 0,
        max_workflows: source.max_workflows ?? 0,
        max_integrations: source.max_integrations ?? 0,
        max_custom_fields: source.max_custom_fields ?? 0,
      });
    }
  }, [item?.limits, item?.quotas]);

  const handleSuspend = async () => {
    if (!id || !suspendReason.trim()) return;
    await store.SuspendTenant(id, { reason_detail: suspendReason });
    setShowSuspendForm(false);
    setSuspendReason('');
    crud.GetTenant(id).catch(() => {});
    store.GetTenantSuspensionHistory(id).then((res: any) => {
      const items = res?.suspensions?.items;
      setSuspensionHistory(Array.isArray(items) ? items : []);
    }).catch(() => {});
  };

  const handleReactivate = async () => {
    if (!id) return;
    await store.ReactivateTenant(id, {});
    crud.GetTenant(id).catch(() => {});
  };

  const handleSaveQuotas = async () => {
    if (!id) return;
    await store.UpdateTenantQuota(id, { limits: quotaForm as any });
    crud.GetTenant(id).catch(() => {});
  };

  const updateQuotaField = (field: string, value: string) => {
    const num = parseInt(value, 10);
    setQuotaForm((prev) => ({
      ...prev,
      [field]: isNaN(num) ? 0 : num,
    }));
  };

  const isSuspended =
    item?.status === 'SUSPENDED' || item?.status === 'TENANT_STATUS_SUSPENDED';
  const rawStatus = normalizeStatus(String(item?.status ?? ''));
  const rawType = normalizeType(String(item?.type ?? ''));
  const rawIsolation = normalizeIsolation(String(item?.isolation_level ?? ''));

  if (store.isLoading || crud.isLoadingItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (crud.error) {
    return (
      <>
        <ErrorAlert error={crud.error} />
        <Link
          to="/admin/tenants"
          className="mt-2 inline-block text-sm underline text-primary"
        >
          {t('tenant.admin.backToTenants')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link
          to="/admin/tenants"
          className="mt-2 inline-block text-sm underline text-primary"
        >
          {t('tenant.admin.backToTenants')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/admin/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.admin.breadcrumbAdmin')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/admin/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.admin.breadcrumbTenants')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? 'Tenant')}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">
              {String(item.name ?? '')}
            </h1>
            <StatusBadge
              variant={mapTenantStatusToVariant(item.status)}
              label={
                rawStatus
                  ? rawStatus.charAt(0) + rawStatus.slice(1).toLowerCase()
                  : 'Unknown'
              }
            />
            <Badge variant={mapTenantTypeToVariant(item.type) as any} size="sm">
              {rawType
                ? rawType.charAt(0) + rawType.slice(1).toLowerCase()
                : '--'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {String(item.slug ?? '')} &middot; {t('tenant.admin.created')}{' '}
            {formatDate(item.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          {isSuspended ? (
            <Button variant="primary" onClick={handleReactivate}>
              {t('tenant.admin.reactivate')}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={() => setShowSuspendForm(!showSuspendForm)}
            >
              {t('tenant.admin.suspend')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            {t('tenant.admin.viewAnalytics')}
          </Button>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Suspend form (inline) */}
      {showSuspendForm && !isSuspended && (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">
              {t('tenant.admin.suspendTenant')}
            </CardTitle>
            <CardDescription>
              {t('tenant.admin.suspendDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('tenant.admin.suspensionReason')}
              </label>
              <Input
                placeholder={t('tenant.admin.suspensionReasonPlaceholder')}
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                fullWidth={false}
                className="w-full"
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowSuspendForm(false);
                setSuspendReason('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={!suspendReason.trim()}
              onClick={handleSuspend}
            >
              {t('tenant.admin.confirmSuspend')}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Info grid: 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: General Info */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t('tenant.admin.generalInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              {[
                [t('tenant.admin.fieldName'), String(item.name ?? '--')],
                [t('tenant.admin.fieldSlug'), String(item.slug ?? '--')],
                [
                  t('tenant.admin.fieldDescription'),
                  String(item.description ?? '--'),
                ],
                [t('tenant.admin.fieldRegion'), String(item.region ?? '--')],
                [
                  t('tenant.admin.fieldType'),
                  rawType ? rawType.charAt(0) + rawType.slice(1).toLowerCase() : '--',
                ],
                [
                  t('tenant.admin.fieldIsolation'),
                  rawIsolation
                    ? rawIsolation
                        .split('_')
                        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
                        .join(' ')
                    : '--',
                ],
                [
                  t('tenant.admin.fieldOwner'),
                  String(item.owner?.name || item.owner || '--'),
                ],
                [
                  t('tenant.admin.fieldCreated'),
                  formatDate(item.created_at),
                ],
                [
                  t('tenant.admin.fieldUpdated'),
                  formatDate(item.updated_at),
                ],
              ].map(([label, value]) => (
                <div key={String(label)} className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">
                    {String(label)}
                  </dt>
                  <dd className="text-sm text-foreground col-span-2">
                    {String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        {/* Right: Current Status */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t('tenant.admin.currentStatus')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('tenant.admin.fieldStatus')}
                </dt>
                <dd className="text-sm col-span-2">
                  <StatusBadge
                    variant={mapTenantStatusToVariant(item.status)}
                    label={
                      rawStatus
                        ? rawStatus.charAt(0) + rawStatus.slice(1).toLowerCase()
                        : 'Unknown'
                    }
                    size="sm"
                  />
                </dd>
              </div>
              {isSuspended && (
                <>
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">
                      {t('tenant.admin.suspendedAt')}
                    </dt>
                    <dd className="text-sm text-foreground col-span-2">
                      {formatDate(item.suspended_at)}
                    </dd>
                  </div>
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">
                      {t('tenant.admin.suspensionReason')}
                    </dt>
                    <dd className="text-sm text-foreground col-span-2">
                      {String(
                        item.suspension_reason ||
                          item.metadata?.suspension_reason ||
                          '--',
                      )}
                    </dd>
                  </div>
                </>
              )}
            </dl>

            {/* Quick Actions */}
            <div className="mt-6 pt-4 border-t border-border space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                {t('tenant.admin.quickActions')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {isSuspended ? (
                  <Button size="sm" variant="primary" onClick={handleReactivate}>
                    {t('tenant.admin.reactivate')}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => setShowSuspendForm(true)}
                  >
                    {t('tenant.admin.suspend')}
                  </Button>
                )}
                <Link to={`/admin/tenants/${id}/members`}>
                  <Button size="sm" variant="secondary">
                    {t('tenant.admin.viewMembers')}
                  </Button>
                </Link>
                <Link to={`/admin/tenants/${id}/audit`}>
                  <Button size="sm" variant="secondary">
                    {t('tenant.admin.viewAuditLog')}
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Summary */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t('tenant.admin.analyticsSummary')}
          </CardTitle>
          <CardDescription>
            {t('tenant.admin.analyticsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricTotalMembers')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {analytics?.total_members ?? item.member_count ?? '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricActiveMembers')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {analytics?.active_members ?? '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricActivityRate')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {formatPercent(analytics?.member_activity_rate)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricApiCalls')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {analytics?.total_api_calls != null
                  ? analytics.total_api_calls.toLocaleString()
                  : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricStorage')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {formatBytes(analytics?.storage_bytes_used)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricLogins')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {analytics?.total_logins != null
                  ? analytics.total_logins.toLocaleString()
                  : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricMrr')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(analytics?.mrr)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('tenant.admin.metricDau')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {analytics?.avg_daily_active_users != null
                  ? analytics.avg_daily_active_users.toLocaleString()
                  : '--'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotas */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.admin.quotas')}</CardTitle>
          <CardDescription>
            {t('tenant.admin.quotasDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                key: 'max_members',
                label: t('tenant.admin.quotaMaxMembers'),
              },
              {
                key: 'max_products',
                label: t('tenant.admin.quotaMaxProducts'),
              },
              {
                key: 'max_api_calls_per_month',
                label: t('tenant.admin.quotaMaxApi'),
              },
              {
                key: 'max_storage_bytes',
                label: t('tenant.admin.quotaMaxStorage'),
              },
              {
                key: 'max_bandwidth_bytes',
                label: t('tenant.admin.quotaMaxBandwidth'),
              },
              {
                key: 'max_workflows',
                label: t('tenant.admin.quotaMaxWorkflows'),
              },
              {
                key: 'max_integrations',
                label: t('tenant.admin.quotaMaxIntegrations'),
              },
              {
                key: 'max_custom_fields',
                label: t('tenant.admin.quotaMaxCustomFields'),
              },
            ].map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {label}
                </label>
                <Input
                  type="number"
                  value={String(quotaForm[key] ?? 0)}
                  onChange={(e) => updateQuotaField(key, e.target.value)}
                  fullWidth={false}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveQuotas}>
            {t('tenant.admin.saveQuotas')}
          </Button>
        </CardFooter>
      </Card>

      {/* Suspension History */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t('tenant.admin.suspensionHistory')}
          </CardTitle>
          <CardDescription>
            {t('tenant.admin.suspensionHistoryDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t('tenant.admin.shReason')}
                </TableHead>
                <TableHead>
                  {t('tenant.admin.shDetail')}
                </TableHead>
                <TableHead>
                  {t('tenant.admin.shSuspendedBy')}
                </TableHead>
                <TableHead>
                  {t('tenant.admin.shSuspendedAt')}
                </TableHead>
                <TableHead>
                  {t('tenant.admin.shReactivatedAt')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suspensionHistory.length === 0 ? (
                <TableEmpty
                  colSpan={5}
                  message={t('tenant.admin.noSuspensions')}
                />
              ) : (
                suspensionHistory.map((entry: any, index: number) => (
                  <TableRow key={entry.id || index}>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {String(entry.reason ?? '--')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {String(entry.reason_detail ?? entry.detail ?? '--')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {String(
                          entry.suspended_by?.name ||
                            entry.suspended_by ||
                            '--',
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(entry.suspended_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(entry.reactivated_at)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default AdminTenantDetailPage;
